import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, FileText, Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  first_name: z.string().min(1, 'First name is required').max(100),
  last_name: z.string().min(1, 'Last name is required').max(100),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  email: z.string().email('Invalid email address'),
  agreement_accepted: z.boolean().refine((val) => val === true, {
    message: 'You must read and accept the agreement'
  })
});

const MembershipModal = ({ open, onOpenChange }) => {
  const [agreementText, setAgreementText] = useState('');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasReadFull, setHasReadFull] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const scrollRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      agreement_accepted: false
    }
  });

  const agreementAccepted = watch('agreement_accepted');
  const firstName = watch('first_name');
  const lastName = watch('last_name');
  const phone = watch('phone');
  const email = watch('email');

  // Check if all required fields are filled
  const allFieldsFilled = firstName && lastName && phone && email && 
                          firstName.trim() !== '' && lastName.trim() !== '' && 
                          phone.trim() !== '' && email.trim() !== '';

  useEffect(() => {
    const fetchAgreementText = async () => {
      try {
        const response = await axios.get(`${API}/agreement-text`);
        setAgreementText(response.data.text);
      } catch (error) {
        console.error('Failed to load agreement text:', error);
        toast.error('Failed to load agreement text');
      }
    };

    if (open) {
      fetchAgreementText();
      setHasReadFull(false);
      setScrollProgress(0);
    }
  }, [open]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setScrollProgress(Math.min(progress, 100));

      // User has scrolled to bottom
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        setHasReadFull(true);
      }
    }
  };

  const onSubmit = async (data) => {
    if (!hasReadFull) {
      toast.error('Please scroll to the bottom of the agreement to continue');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/membership-agreement`, data);
      
      if (response.data) {
        setShowSuccess(true);
        toast.success('Thank you for joining CME!');
        
        setTimeout(() => {
          setShowSuccess(false);
          onOpenChange(false);
          reset();
          setHasReadFull(false);
          setScrollProgress(0);
        }, 3000);
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit agreement. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="success-modal">
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center" data-testid="success-content">
            <div className="mb-6 relative">
              <div className="absolute inset-0 bg-cme-gold/20 blur-2xl rounded-full" />
              <CheckCircle2 className="w-20 h-20 text-cme-red relative" data-testid="success-icon" />
            </div>
            <h2 className="text-3xl font-serif font-bold text-black mb-3" data-testid="success-title">
              Signed & Sealed
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed" data-testid="success-message">
              Thank you for joining CME. Your membership agreement has been received.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl w-full max-h-[90vh] p-0 overflow-hidden grid grid-cols-1 md:grid-cols-2"
        data-testid="membership-modal"
      >
        {/* Left Pane - Agreement Text */}
        <div className="bg-white p-6 md:p-10 border-r border-gray-200 flex flex-col max-h-[90vh] md:max-h-full">
          <div className="mb-4 flex-shrink-0">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-cme-red" />
              <h2 className="text-2xl font-serif font-bold text-black" data-testid="agreement-title">
                Membership Agreement
              </h2>
            </div>
            <Progress value={scrollProgress} className="h-1.5 bg-gray-200" data-testid="scroll-progress">
              <div className="h-full bg-cme-red transition-all" style={{ width: `${scrollProgress}%` }} />
            </Progress>
            {!hasReadFull && (
              <p className="text-xs text-gray-600 mt-2 flex items-center gap-1" data-testid="scroll-instruction">
                <Lock className="w-3 h-3 text-cme-gold" />
                Please scroll to the bottom to continue
              </p>
            )}
            {hasReadFull && (
              <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1 font-semibold">
                ✓ Agreement read completely
              </p>
            )}
          </div>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-scroll pr-4 scrollbar-thin min-h-0"
            data-testid="agreement-scroll-area"
            style={{ maxHeight: 'calc(90vh - 200px)' }}
          >
            <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap font-serif pb-4">
              {agreementText}
            </div>
          </div>
        </div>

        {/* Right Pane - Form */}
        <div className="bg-gray-50 p-6 md:p-10 flex flex-col justify-center overflow-y-auto">
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_cme-join-modal/artifacts/wocpjpx8_CME2020-HORIZONTAL.jpg" 
              alt="CME Logo"
              className="h-16 w-auto mb-4"
            />
            <h3 className="text-xl font-sans font-semibold text-black" data-testid="form-title">
              Sign Agreement
            </h3>
            <p className="text-sm text-gray-600">Enter your information to electronically sign</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" data-testid="membership-form">
            <div>
              <Label htmlFor="first_name" className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
                First Name *
              </Label>
              <Input
                id="first_name"
                {...register('first_name')}
                className="h-12 bg-white border-gray-300 focus:border-cme-red focus:ring-1 focus:ring-cme-red"
                placeholder="John"
                data-testid="first-name-input"
              />
              {errors.first_name && (
                <p className="text-xs text-cme-red mt-1" data-testid="first-name-error">{errors.first_name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="last_name" className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
                Last Name *
              </Label>
              <Input
                id="last_name"
                {...register('last_name')}
                className="h-12 bg-white border-gray-300 focus:border-cme-red focus:ring-1 focus:ring-cme-red"
                placeholder="Doe"
                data-testid="last-name-input"
              />
              {errors.last_name && (
                <p className="text-xs text-cme-red mt-1" data-testid="last-name-error">{errors.last_name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="phone" className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
                Phone *
              </Label>
              <Input
                id="phone"
                {...register('phone')}
                className="h-12 bg-white border-gray-300 focus:border-cme-red focus:ring-1 focus:ring-cme-red"
                placeholder="(555) 123-4567"
                data-testid="phone-input"
              />
              {errors.phone && (
                <p className="text-xs text-cme-red mt-1" data-testid="phone-error">{errors.phone.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-gray-700 mb-2 block uppercase tracking-wide">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                {...register('email')}
                className="h-12 bg-white border-gray-300 focus:border-cme-red focus:ring-1 focus:ring-cme-red"
                placeholder="john@example.com"
                data-testid="email-input"
              />
              {errors.email && (
                <p className="text-xs text-cme-red mt-1" data-testid="email-error">{errors.email.message}</p>
              )}
            </div>

            <div className="flex items-start space-x-3 pt-2">
              <Checkbox
                id="agreement_accepted"
                checked={agreementAccepted}
                onCheckedChange={(checked) => setValue('agreement_accepted', checked)}
                disabled={!hasReadFull || !allFieldsFilled}
                className="mt-1 border-cme-red data-[state=checked]:bg-cme-red"
                data-testid="agreement-checkbox"
              />
              <div className="flex-1">
                <Label
                  htmlFor="agreement_accepted"
                  className="text-sm text-gray-700 leading-relaxed cursor-pointer"
                >
                  I have read and agree to the CME Membership Agreement and Privacy Policy.
                  {(!hasReadFull || !allFieldsFilled) && (
                    <span className="block text-xs text-cme-gold mt-1 flex items-center gap-1 font-semibold">
                      <Lock className="w-3 h-3" />
                      {!allFieldsFilled && 'Fill all fields above, then '}
                      {!hasReadFull && 'scroll agreement to bottom'}
                    </span>
                  )}
                </Label>
                {errors.agreement_accepted && (
                  <p className="text-xs text-cme-red mt-1" data-testid="agreement-error">
                    {errors.agreement_accepted.message}
                  </p>
                )}
              </div>
            </div>

            <Button
              type="submit"
              disabled={!agreementAccepted || isSubmitting}
              className="w-full h-12 bg-cme-red text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 font-semibold text-base shadow-lg hover:shadow-cme-red/30 transition-all duration-300 transform active:scale-95 border-2 border-cme-gold"
              data-testid="submit-button"
            >
              {isSubmitting ? 'Submitting...' : 'Sign & Submit Agreement'}
            </Button>

            <p className="text-xs text-center text-gray-500 pt-2">
              By submitting, you are providing an electronic signature
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MembershipModal;